// ── Shared utilities ──

import { marked } from 'marked';
import { getToken } from './api.js';

marked.setOptions({ breaks: true, gfm: true });

export function escapeHtml(s) {
  const el = document.createElement('span');
  el.textContent = s || '';
  return el.innerHTML;
}

export function renderMarkdown(text) {
  if (!text) return '';
  return marked.parse(text);
}

export function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

const AVATAR_COLORS = [
  'var(--c-rosewater)', 'var(--c-flamingo)', 'var(--c-pink)', 'var(--c-mauve)',
  'var(--c-red)', 'var(--c-maroon)', 'var(--c-peach)', 'var(--c-yellow)',
  'var(--c-green)', 'var(--c-teal)', 'var(--c-sky)', 'var(--c-blue)', 'var(--c-lavender)',
];

export function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function avatarBgColor(userId) {
  return AVATAR_COLORS[(userId || 0) % AVATAR_COLORS.length];
}

export function renderAvatarHtml(user, sizePx) {
  const s = sizePx || 24;
  if (!user) {
    // The plus is a CSS-drawn cross (.avatar-plus, styles.css), not a text
    // glyph — a literal '+' character sits on the font's math axis, not the
    // em-box center, so no line-height/font-size tuning centers it reliably
    // across fonts/platforms. See tt-web-auth.md § Signup Wizard.
    return `<div class="avatar avatar-placeholder" style="width:${s}px;height:${s}px;"><span class="avatar-plus" style="--plus-size:${Math.round(s * 0.55)}px;"></span></div>`;
  }
  // (TPT76) avatar_src (server-resolved precedence, api/src/lib/avatar.js) wins
  // over avatar_url — every user/member row GET /me and GET /members return
  // already carries it (TPT75); falls back to avatar_url for a synthetic caller
  // (e.g. login.js's signup preview passes { avatar_url: <data URL> } directly).
  const src = user.avatar_src || user.avatar_url;
  if (src) {
    return `<img class="avatar" src="${escapeHtml(src)}" alt="${escapeHtml(user.name || '')}" style="width:${s}px;height:${s}px;">`;
  }
  const initials = getInitials(user.name || user.email);
  const bg = avatarBgColor(user.id || user.user_id);
  return `<div class="avatar avatar-initials" style="width:${s}px;height:${s}px;font-size:${Math.round(s * 0.42)}px;background:${bg};">${escapeHtml(initials)}</div>`;
}

// ── Due-date pill ──

// `closed` is the set of status names that mean "done, don't flag as overdue" — pass the
// caller's resolved closed-role names (lib/statuses.js's closedNames()) when available;
// defaults to the legacy pair for callers with no project status context (this module has
// none of its own — see lib/statuses.js § C1187 for why the registry isn't imported here).
export function renderDuePill(task, closed = ['completed', 'canceled']) {
  if (!task.due_date) return '';
  const due = new Date(task.due_date + 'T00:00');
  if (isNaN(due)) return '';
  const label = due.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const closedSet = closed instanceof Set ? closed : new Set(closed);
  const overdue = task.due_date < today && !closedSet.has(task.status);
  return `<span class="card-due${overdue ? ' overdue' : ''}" title="Due ${escapeHtml(label)}">${escapeHtml(label)}</span>`;
}

// Mirror of ai/todo/server/src/client/utils.js shortModelName() (C1133/C1302) — keep in
// sync; the two frontends are separate bundles so this can't be a shared import.
// 'openrouter/anthropic/claude-3.5-sonnet' → 'claude-3.5-sonnet' (full id kept in title=).
export function shortModelName(model) {
  const parts = String(model).split('/').filter(Boolean);
  return parts[parts.length - 1] || String(model);
}

// ── Agent assignee badge ──

export function renderAgentBadge(task, members, opts = {}) {
  const a = task.agent_assignee;
  if (!a) return '';
  // TPT36 — { overlap: true } adds .agent-overlap, which repositions the badge (styles.css)
  // to cap the bottom-right quadrant of the sibling avatar inside .card-avatar-wrap instead
  // of the plain card-anchored corner placement.
  const cls = opts.overlap ? ' agent-overlap' : '';
  if (a === 'claude') {
    return `<div class="assignee-badge assignee-badge--claude${cls}" title="Claude Code"><svg width="10" height="10" viewBox="0 0 248 248" aria-hidden="true" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M52.4285 162.873L98.7844 136.879L99.5485 134.602L98.7844 133.334H96.4921L88.7237 132.862L62.2346 132.153L39.3113 131.207L17.0249 130.026L11.4214 128.844L6.2 121.873L6.7094 118.447L11.4214 115.257L18.171 115.847L33.0711 116.911L55.485 118.447L71.6586 119.392L95.728 121.873H99.5485L100.058 120.337L98.7844 119.392L97.7656 118.447L74.5877 102.732L49.4995 86.1905L36.3823 76.62L29.3779 71.7757L25.8121 67.2858L24.2839 57.3608L30.6515 50.2716L39.3113 50.8623L41.4763 51.4531L50.2636 58.1879L68.9842 72.7209L93.4357 90.6804L97.0015 93.6343L98.4374 92.6652L98.6571 91.9801L97.0015 89.2625L83.757 65.2772L69.621 40.8192L63.2534 30.6579L61.5978 24.632C60.9565 22.1032 60.579 20.0111 60.579 17.4246L67.8381 7.49965L71.9133 6.19995L81.7193 7.49965L85.7946 11.0443L91.9074 24.9865L101.714 46.8451L116.996 76.62L121.453 85.4816L123.873 93.6343L124.764 96.1155H126.292V94.6976L127.566 77.9197L129.858 57.3608L132.15 30.8942L132.915 23.4505L136.608 14.4708L143.994 9.62643L149.725 12.344L154.437 19.0788L153.8 23.4505L150.998 41.6463L145.522 70.1215L141.957 89.2625H143.994L146.414 86.7813L156.093 74.0206L172.266 53.698L179.398 45.6635L187.803 36.802L193.152 32.5484H203.34L210.726 43.6549L207.415 55.1159L196.972 68.3492L188.312 79.5739L175.896 96.2095L168.191 109.585L168.882 110.689L170.738 110.53L198.755 104.504L213.91 101.787L231.994 98.7149L240.144 102.496L241.036 106.395L237.852 114.311L218.495 119.037L195.826 123.645L162.07 131.592L161.696 131.893L162.137 132.547L177.36 133.925L183.855 134.279H199.774L229.447 136.524L237.215 141.605L241.8 147.867L241.036 152.711L229.065 158.737L213.019 154.956L175.45 145.977L162.587 142.787H160.805V143.85L171.502 154.366L191.242 172.089L215.82 195.011L217.094 200.682L213.91 205.172L210.599 204.699L188.949 188.394L180.544 181.069L161.696 165.118H160.422V166.772L164.752 173.152L187.803 207.771L188.949 218.405L187.294 221.832L181.308 223.959L174.813 222.777L161.187 203.754L147.305 182.486L136.098 163.345L134.745 164.2L128.075 235.42L125.019 239.082L117.887 241.8L111.902 237.31L108.718 229.984L111.902 215.452L115.722 196.547L118.779 181.541L121.58 162.873L123.291 156.636L123.14 156.219L121.773 156.449L107.699 175.752L86.304 204.699L69.3663 222.777L65.291 224.431L58.2867 220.768L58.9235 214.27L62.8713 208.48L86.304 178.705L100.44 160.155L109.551 149.507L109.462 147.967L108.959 147.924L46.6977 188.512L35.6182 189.93L30.7788 185.44L31.4156 178.115L33.7079 175.752L52.4285 162.873Z"/></svg></div>`;
  }
  if (a === 'codex') {
    return `<div class="assignee-badge assignee-badge--codex${cls}" title="Codex"><svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true" fill="#fff"><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/></svg></div>`;
  }
  if (a === 'pi') {
    // (C1302) pinned model id in the tooltip, not the bare 'Pi'.
    const piTitle = task.pi_model ? shortModelName(task.pi_model) : 'Pi';
    return `<div class="assignee-badge assignee-badge--pi${cls}" title="${escapeHtml(piTitle)}"><svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true" fill="#fff"><path d="M4 7h16v2.2h-2.6l-.9 9.4a1.6 1.6 0 0 1-3.18-.16l.68-9.24H9.9l-.7 9.3a1.6 1.6 0 0 1-3.18-.18l.68-9.12H4V7z"/></svg></div>`;
  }
  const user = task.assignee && members
    ? members.find(m => (m.user_id || m.id) === task.assignee)
    : null;
  const initials = user ? getInitials(user.name || user.email) : '';
  if (initials) {
    return `<div class="assignee-badge assignee-badge--human${cls}" title="${escapeHtml(user.name || user.email)}">${escapeHtml(initials)}</div>`;
  }
  return `<div class="assignee-badge assignee-badge--human${cls}" title="Human"><svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" fill="#fff"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></svg></div>`;
}

// ── Task type helpers ──

export const TYPES = ['epic', 'story', 'task', 'bug'];
export const TYPE_COLORS = { epic: 'var(--c-mauve)', story: 'var(--c-blue)', task: 'var(--c-overlay1)', bug: 'var(--c-red)' };
export const TYPE_LABELS = { epic: 'Epic', story: 'Story', task: 'Task', bug: 'Bug' };

export function getTypeIcon(type, size) {
  const s = size || 18;
  const color = TYPE_COLORS[type] || TYPE_COLORS.task;
  return `<svg width="${s}" height="${s}" viewBox="0 0 18 18"><circle cx="9" cy="9" r="7" fill="${color}" opacity="0.2"/><circle cx="9" cy="9" r="4" fill="${color}"/></svg>`;
}

export async function uploadImage(projectId, file, taskKey = null) {
  console.debug('[uploadImage] start', file.name, file.size);
  try {
    const base64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = String(r.result).split(',')[1] || '';
        console.debug('[uploadImage] encoded', result.length, 'chars');
        resolve(result);
      };
      r.onerror = () => reject(new Error('Read failed'));
      r.readAsDataURL(file);
    });
    const token = getToken();
    const res = await fetch(`/api/projects/${projectId}/images`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ filename: file.name, mimeType: file.type, data: base64, taskKey }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || res.statusText);
    }
    const payload = await res.json();
    console.debug('[uploadImage] done', payload.url);
    return payload;
  } catch (err) {
    console.error('[uploadImage] failed', err);
    throw err;
  }
}

export function insertAtCursor(el, text) {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  el.value = el.value.slice(0, start) + text + el.value.slice(end);
  const pos = start + text.length;
  el.selectionStart = el.selectionEnd = pos;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

// `closed` — see renderDuePill() above for the same contract (caller-supplied resolved
// closed-role names, defaulting to the legacy pair).
export function groupBySprint(tasks, closed = ['completed', 'canceled']) {
  const closedSet = closed instanceof Set ? closed : new Set(closed);
  const priorityMap = new Map();
  for (const t of tasks) {
    if (!priorityMap.has(t.priority)) priorityMap.set(t.priority, []);
    priorityMap.get(t.priority).push(t);
  }
  const priorities = [...priorityMap.keys()].sort((a, b) => b - a);
  return priorities.map((priority) => {
    const sprintTasks = priorityMap.get(priority);
    return {
      sprintNumber: priority,
      priority,
      tasks: sprintTasks,
      allDone: sprintTasks.every(t => closedSet.has(t.status)),
    };
  });
}

// Sprint <option> helpers — union of sprint-table numbers + in-use task priorities,
// sorted desc, labeled by the real sprint number (task.priority === sprints.number).
export function sprintOptionsHtml(sprints, tasks, currentPriority, label = 'Sprint') {
  const nums = new Set();
  for (const s of (sprints || [])) if (s.number > 0) nums.add(s.number);
  for (const t of (tasks || [])) if (t.priority > 0) nums.add(t.priority);
  if (currentPriority > 0) nums.add(currentPriority);
  return [...nums].sort((a, b) => b - a)
    .map(n => `<option value="${n}" ${n === currentPriority ? 'selected' : ''}>${label} ${n}</option>`)
    .join('');
}

export function nextSprintNumber(sprints, tasks) {
  const nums = [...(sprints || []).map(s => s.number), ...(tasks || []).map(t => t.priority)];
  return Math.max(0, ...nums) + 1;
}

// Returns false when the task is assigned to someone other than currentUser,
// i.e. this user is NOT allowed to start (transition to in_progress) the task.
export function canStartTask(task, currentUser) {
  return !(task && task.assignee && currentUser && task.assignee !== currentUser.id);
}

// Status values this user may choose for the task. Hides the in-progress-role status
// when the task is assigned to another member — unless it's already that status, so the
// current value is never dropped from the control. `inProgressName` is this project's
// current is_in_progress-role status name (C1182; resolve via `roleStatus()` in
// lib/statuses.js) — falls back to the legacy literal 'in_progress' if omitted, so any
// caller not yet passing it degrades to the old behavior instead of hiding nothing.
export function availableStatuses(allStatuses, task, currentUser, inProgressName = 'in_progress') {
  return allStatuses.filter(s =>
    s !== inProgressName || task.status === inProgressName || canStartTask(task, currentUser)
  );
}
